<template>
  <div ref="modalEl" class="modal fade" id="openingBalanceModal" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Set Opening Capital</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <div v-if="error" class="alert alert-danger py-2 mb-3">{{ error }}</div>

          <div class="row g-2">
            <!-- Member -->
            <div class="col-md-6">
              <label class="form-label">Member</label>

              <!-- locked mode (openForMember) -->
              <div v-if="lockMember" class="border rounded p-2 bg-light">
                <div class="fw-semibold">{{ member?.name || "—" }}</div>
                <div class="text-muted small">
                  {{ member?.phone || member?.email || "—" }}
                </div>
              </div>

              <!-- dropdown mode (openBlank) -->
              <select v-else v-model="form.user_id" class="form-select">
                <option value="">Select member</option>
                <option v-for="m in members" :key="m.id" :value="m.id">
                  {{ m.name }} ({{ m.phone || "-" }})
                </option>
              </select>
            </div>

            <!-- Amount -->
            <div class="col-md-6">
              <label class="form-label">Opening capital amount</label>
              <input v-model.number="form.amount" type="number" min="0" step="0.01" class="form-control" placeholder="0" />
              <div class="text-muted small mt-1">This becomes the member’s starting savings balance.</div>
            </div>

            <!-- As-of period -->
            <div class="col-md-6">
              <label class="form-label">As-of period</label>
              <input ref="asOfInput" type="text" class="form-control" placeholder="Pick month..." />
              <div class="text-muted small mt-1">Saved as <strong>{{ form.as_of_period || "—" }}</strong> (YYYY-MM)</div>
            </div>

            <!-- Note -->
            <div class="col-md-6">
              <label class="form-label">Note (optional)</label>
              <input v-model="form.note" type="text" class="form-control" placeholder="e.g. Imported from 2025 ledger" />
            </div>
          </div>

          <div class="alert alert-warning mt-3 mb-0">
            <div class="fw-semibold mb-1">Audit tip</div>
            <div class="small">
              This creates an <strong>opening_balance</strong> entry in transactions for traceability.
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
          <button class="btn btn-primary" :disabled="saving || !canSave" @click="save">
            {{ saving ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/services/http";
import { Modal } from "bootstrap";

import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import "flatpickr/dist/plugins/monthSelect/style.css";

export default {
  name: "OpeningBalanceModal",
  props: {
    members: { type: Array, default: () => [] },
  },
  data() {
    return {
      modal: null,
      fp: null,

      saving: false,
      error: "",

      lockMember: false,
      member: null,

      form: {
        user_id: "",
        amount: "",
        as_of_period: "",
        note: "",
      },
    };
  },
  computed: {
    canSave() {
      return !!this.form.user_id && this.form.as_of_period && this.form.amount !=="" && this.form.amount >= 0;
    }
  },
  methods: {
    monthKeyToday() {
      const t = new Date();
      const yyyy = t.getFullYear();
      const mm = String(t.getMonth() + 1).padStart(2, "0");
      return `${yyyy}-${mm}`;
    },
    destroyPicker() {
      if (this.fp) {
        this.fp.destroy();
        this.fp = null;
      }
    },
    initPicker() {
      this.destroyPicker();
      const el = this.$refs.asOfInput;
      if (!el) return;

      const initial = this.form.as_of_period || this.monthKeyToday();

      this.fp = flatpickr(el, {
        plugins: [
          monthSelectPlugin({
            shorthand: true,
            dateFormat: "Y-m",
            altFormat: "F Y",
            theme: "light",
          }),
        ],
        disableMobile: true,
        defaultDate: initial,
        onReady: (_, dateStr) => {
          this.form.as_of_period = dateStr || initial;
        },
        onChange: (_, dateStr) => {
          this.form.as_of_period = dateStr;
        },
      });

      this.fp.setDate(initial, true, "Y-m");
    },

    // Call this from a member row or bulk table:
    // this.$refs.openingBalanceModal.openForMember(user, period)
    openForMember(user, period = "") {
      this.error = "";
      this.lockMember = true;
      this.member = user || null;

      this.form = {
        user_id: user?.id || "",
        amount: "",
        as_of_period: period || this.monthKeyToday(),
        note: "",
      };

      this.modal.show();
      this.$nextTick(() => this.initPicker());
    },

    // Optional: open with dropdown selection (like a standalone admin page)
    openBlank(period = "") {
      this.error = "";
      this.lockMember = false;
      this.member = null;

      this.form = {
        user_id: "",
        amount: "",
        as_of_period: period || this.monthKeyToday(),
        note: "",
      };

      this.modal.show();
      this.$nextTick(() => this.initPicker());
    },

    async save() {
      this.saving = true;
      this.error = "";

      try {
        if (!this.form.user_id) throw new Error("Select a member.");
        if (this.form.amount === "" || this.form.amount === null) throw new Error("Enter an amount.");
        if (!this.form.as_of_period) throw new Error("Pick an as-of period.");

        const payload = {
          user_id: this.form.user_id,
          amount: this.form.amount,
          as_of_period: this.form.as_of_period,
          note: this.form.note || null,
        };

        const res = await http.post("/opening-balances", payload);

        this.modal.hide();
        this.$emit("saved", res.data.data);
      } catch (e) {
        if (e instanceof Error && !e?.response) {
          this.error = e.message;
        } else {
          const msg = e?.response?.data?.message || "Failed to save opening capital.";
          const errs = e?.response?.data?.errors;

          if (errs) {
            const firstKey = Object.keys(errs)[0];
            this.error = errs[firstKey]?.[0] || msg;
          } else {
            this.error = msg;
          }
        }
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    const el = this.$refs.modalEl;
  if (!el) return;

  this.modal = new Modal(el);

  el.addEventListener("hidden.bs.modal", () => {
    this.destroyPicker();
    this.error = "";
    this.saving = false;
  });
  },
  beforeUnmount() {
    this.destroyPicker();
  },
};
</script>

<style scoped>
:deep(.flatpickr-input[readonly]) {
  background-color: #fff;
}
</style>
